import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { AddOrEditMatterTaskPlanModal } from "@/components/features/settings/defaults/matter-task-plans/AddOrEditMatterTaskPlanModal";
import { MatterTaskPlanContextMenu } from "@/components/features/settings/defaults/matter-task-plans/MatterTaskPlanContextMenu";
import { TaskPlanTasksPanel } from "@/components/features/settings/defaults/matter-task-plans/TaskPlanTasksPanel";
import {
  ActionEmptyState,
  ActionConfirmationModal,
  Badge,
  Button,
  Card,
  CardContent,
  LoadingSpinner,
  MultiSelect,
  Text,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { retrieveMatterTaskPlansQueryOptions } from "@/lib/data/react-query/matters/matter-task-plan";
import { useDeleteMatterTaskPlan } from "@/lib/data/react-query/matters/matter-task-plan";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  getMatterStatusCategoryLabel,
  type MatterStatusCategoryOption,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterTaskPlan } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { sortBy } from "lodash";
import { ChevronRight, ListChecks, Pencil, Plus } from "lucide-react";
import pluralize from "pluralize";
import { useMemo, useState } from "react";

const TASK_PLAN_TRIGGER_TYPE_OPTIONS = [
  { label: "Task completion", value: "Task Completion" },
  { label: "Action taken", value: "Action Taken" },
  { label: "Matter status", value: "Matter Status" },
];

function TaskPlansSettingsViewInner({
  title,
  addButtonLabel,
  statusCategories,
}: {
  title: string;
  addButtonLabel: string;
  statusCategories: readonly MatterStatusCategoryOption[];
}) {
  const [slideoverOpen, setSlideoverOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activeMatterTaskPlan, setActiveMatterTaskPlan] =
    useState<MatterTaskPlan>();
  const [incidentTypeIds, setTypeIds] = useState<string[]>([]);
  const [statusIds, setStatusIds] = useState<string[]>([]);
  const [triggerTypes, setTriggerTypes] = useState<string[]>([]);
  const [planPendingDelete, setPlanPendingDelete] =
    useState<MatterTaskPlan | null>(null);

  const { hasActionPermission } = useUser();

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const incidentTypes = matterSettingsData?.data.incidentTypes ?? [];
  const matterStatuses = useMemo(
    () =>
      (matterSettingsData?.data.statuses ?? []).filter((status) =>
        statusCategories.some((category) => category === status.categoryId),
      ),
    [matterSettingsData?.data.statuses, statusCategories],
  );
  const matterRoles = matterSettingsData?.data.roles ?? [];

  const filters = useMemo(
    () => ({ incidentTypeIds, statusIds, triggerTypes }),
    [statusIds, triggerTypes, incidentTypeIds],
  );
  const {
    data: taskPlansData,
    isLoading: isTaskPlansLoading,
    isError: isTaskPlansError,
    isFetching: isTaskPlansFetching,
  } = useQuery(retrieveMatterTaskPlansQueryOptions(filters));

  const taskPlans = useMemo(
    () =>
      sortBy(taskPlansData?.data ?? [], (p) =>
        new Date(p.dateCreated).getTime(),
      ),
    [taskPlansData?.data],
  );
  const hasActiveFilters =
    incidentTypeIds.length > 0 ||
    statusIds.length > 0 ||
    triggerTypes.length > 0;
  const isLoading = isMatterSettingsLoading || isTaskPlansLoading;
  const isError = isMatterSettingsError || isTaskPlansError;

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTaskPlan,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTaskPlan,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTaskPlan,
    ACTION_PERMISSION_VALUES.delete,
  );
  const deleteMatterTaskPlan = useDeleteMatterTaskPlan();
  const handleTransitionToEdit = (plan: MatterTaskPlan) => {
    setActiveMatterTaskPlan(plan);
    setEditing(true);
  };

  const isReadOnly = editing ? !canUpdate : !canCreate;

  if (isError) {
    return <Text variant="subHeading">{serverErrorFallbackText}</Text>;
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="gap-y-1">
          <Text variant="title">{title}</Text>
        </div>
        {canCreate ? (
          <Button
            variant="foreground"
            size="md"
            className="shrink-0"
            onClick={() => {
              setEditing(false);
              setActiveMatterTaskPlan(undefined);
              setSlideoverOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addButtonLabel}
          </Button>
        ) : null}
      </div>

      <div className="border-border/60 bg-muted/30 mb-6 grid gap-4 rounded-lg border p-4 md:grid-cols-3">
        <div>
          <Text variant="caption" className="mb-1 font-medium">
            Incident types
          </Text>
          <MultiSelect
            options={incidentTypes.map((type) => ({
              label: getIncidentTypeLabel(type),
              value: type.id,
            }))}
            selectedValues={incidentTypeIds}
            setSelectedValues={setTypeIds}
            onValueChange={setTypeIds}
            placeholder="All incident types"
            maxCount={2}
          />
        </div>
        <div>
          <Text variant="caption" className="mb-1 font-medium">
            Matter statuses
          </Text>
          <MultiSelect
            options={matterStatuses.map((status) => ({
              group: getMatterStatusCategoryLabel(status.categoryId),
              label: status.label,
              value: status.id,
            }))}
            selectedValues={statusIds}
            setSelectedValues={setStatusIds}
            onValueChange={setStatusIds}
            placeholder="All matter statuses"
            maxCount={2}
          />
        </div>
        <div>
          <Text variant="caption" className="mb-1 font-medium">
            Trigger types
          </Text>
          <MultiSelect
            options={TASK_PLAN_TRIGGER_TYPE_OPTIONS}
            selectedValues={triggerTypes}
            setSelectedValues={setTriggerTypes}
            onValueChange={setTriggerTypes}
            placeholder="All trigger types"
            maxCount={2}
          />
        </div>
        {incidentTypeIds.length || statusIds.length || triggerTypes.length ? (
          <Button
            variant="ghost"
            size="sm"
            className="w-fit md:col-span-3"
            onClick={() => {
              setTypeIds([]);
              setStatusIds([]);
              setTriggerTypes([]);
            }}
          >
            Clear filters
          </Button>
        ) : null}
      </div>

      <div className="relative">
        {isTaskPlansFetching && taskPlansData ? (
          <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]">
            <LoadingSpinner className="h-6 w-6" />
          </div>
        ) : null}
        {isLoading ? (
          <div className="flex flex-col gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-border/60 shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <div className="h-14 w-full animate-pulse rounded-md bg-neutral-100" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : taskPlans.length === 0 ? (
          <ActionEmptyState
            title={
              hasActiveFilters ? "No task plans found" : "No task plans yet"
            }
            description={
              hasActiveFilters
                ? "Try adjusting or clearing your filters."
                : "Create a task plan to standardize tasks for your incident types."
            }
            actions={
              hasActiveFilters ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setTypeIds([]);
                    setStatusIds([]);
                    setTriggerTypes([]);
                  }}
                >
                  Clear filters
                </Button>
              ) : canCreate ? (
                <Button
                  onClick={() => {
                    setEditing(false);
                    setActiveMatterTaskPlan(undefined);
                    setSlideoverOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {addButtonLabel}
                </Button>
              ) : null
            }
          />
        ) : (
          <Accordion
            type="single"
            collapsible
            className="flex w-full flex-col gap-4"
          >
            {taskPlans.map((plan) => {
              const typeLabels =
                plan.incidentTypeIds?.map((id) => {
                  const mt = incidentTypes.find((m) => m.id === id);
                  return mt ? getIncidentTypeLabel(mt) : id;
                }) ?? [];
              const taskCount = plan.taskCount;

              return (
                <AccordionItem
                  key={plan.id}
                  value={plan.id}
                  className="border-0"
                >
                  <Card className="border-border/60 overflow-hidden shadow-sm">
                    <CardContent className="p-0">
                      <MatterTaskPlanContextMenu
                        plan={plan}
                        canUpdate={canUpdate}
                        canDelete={canDelete}
                        onEdit={() => {
                          setActiveMatterTaskPlan(plan);
                          setEditing(true);
                          setSlideoverOpen(true);
                        }}
                        onDelete={() => {
                          setPlanPendingDelete(plan);
                        }}
                        trigger={
                          <div className="group hover:bg-light-primary/50 flex w-full cursor-pointer items-center gap-3 px-4 py-5 transition-colors sm:px-6">
                            <AccordionTrigger
                              className="min-w-0 flex-1 cursor-pointer items-center py-0 hover:bg-transparent hover:no-underline [&[data-state=open]_.task-plan-chevron]:rotate-90"
                              icon={<></>}
                            >
                              <div className="flex min-w-0 flex-1 items-center gap-3 pr-2 text-left">
                                <ChevronRight
                                  className="task-plan-chevron text-muted-foreground h-4 w-4 shrink-0 self-center transition-transform duration-200"
                                  aria-hidden
                                />
                                <div
                                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[oklab(0.695996_-0.162107_0.0511875/0.1)]"
                                  aria-hidden
                                >
                                  <ListChecks className="h-4 w-4 text-[lab(55.0481_-49.9246_15.93)]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <Text variant="body">{plan.title}</Text>
                                  {plan.description ? (
                                    <Text variant="caption" className="mt-0.5">
                                      {plan.description}
                                    </Text>
                                  ) : null}
                                  {typeLabels.length > 0 ? (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                      {typeLabels.map((label) => (
                                        <Badge
                                          key={label}
                                          variant="outline"
                                          className="border-border/70 bg-muted/55 text-foreground/85 font-normal shadow-none"
                                        >
                                          {label}
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                                <Text
                                  as="span"
                                  variant="subHeading"
                                  className="shrink-0 self-center whitespace-nowrap"
                                >
                                  {taskCount} {pluralize("task", taskCount)}
                                </Text>
                              </div>
                            </AccordionTrigger>

                            {canUpdate ? (
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground focus-visible:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-md opacity-100 transition-[opacity,color,background-color] sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setActiveMatterTaskPlan(plan);
                                  setEditing(true);
                                  setSlideoverOpen(true);
                                }}
                              >
                                <span className="sr-only">
                                  Edit {plan.title}
                                </span>
                                <Pencil className="h-4 w-4" aria-hidden />
                              </button>
                            ) : null}
                          </div>
                        }
                      />
                      <AccordionContent className="px-4 pb-4 sm:px-6">
                        <div className="border-border/60 border-t pt-4">
                          <TaskPlanTasksPanel
                            id={plan.id}
                            tasks={plan.tasks}
                            statuses={matterStatuses}
                            roles={matterRoles}
                            canCreate={canCreate}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                          />
                        </div>
                      </AccordionContent>
                    </CardContent>
                  </Card>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      <AddOrEditMatterTaskPlanModal
        editing={editing}
        open={slideoverOpen}
        setOpen={setSlideoverOpen}
        onTransitionToEdit={handleTransitionToEdit}
        activeMatterTaskPlanId={activeMatterTaskPlan?.id}
        isReadOnly={isReadOnly}
      />
      <ActionConfirmationModal
        title="Are you sure you want to delete this task plan?"
        open={Boolean(planPendingDelete)}
        setOpen={(open) => {
          if (!open) setPlanPendingDelete(null);
        }}
        isActionPending={deleteMatterTaskPlan.isPending}
        afterActionFn={async () => {
          if (!planPendingDelete) return;
          await deleteMatterTaskPlan.mutateAsync({
            id: planPendingDelete.id,
          });
          setPlanPendingDelete(null);
        }}
      />
    </div>
  );
}

export default function TaskPlansSettingsView({
  title = "Task plans",
  addButtonLabel = "Add task plan",
  statusCategories,
}: {
  title?: string;
  addButtonLabel?: string;
  statusCategories: readonly MatterStatusCategoryOption[];
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <TaskPlansSettingsViewInner
        title={title}
        addButtonLabel={addButtonLabel}
        statusCategories={statusCategories}
      />
    </ErrorBoundary>
  );
}
